/**
 * BudgetShortcuts Component
 *
 * Sistema de atalhos de teclado e menu de contexto para orçamentos
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Keyboard,
  Plus,
  Send,
  Edit,
  Copy,
  Download,
  Printer,
  Eye,
  Save,
  X,
  Search,
  FileText,
  DollarSign,
  MessageSquare,
  Clock,
  Zap,
  Star,
  Settings,
  HelpCircle,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Enter,
  Escape
} from 'lucide-react';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  action: () => void;
  category: 'navigation' | 'editing' | 'actions' | 'view';
  enabled?: boolean;
}

interface ContextMenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  action: () => void;
  separator?: boolean;
  disabled?: boolean;
  badge?: string;
}

interface BudgetShortcutsProps {
  budgetId: string;
  shortcuts?: KeyboardShortcut[];
  contextMenuItems?: ContextMenuItem[];
  onCustomShortcut?: (shortcut: Omit<KeyboardShortcut, 'action'>) => void;
  enableKeyboardShortcuts?: boolean;
  enableContextMenu?: boolean;
  showHelpButton?: boolean;
  className?: string;
}

const DEFAULT_SHORTCUTS: Omit<KeyboardShortcut, 'action'>[] = [
  // Navigation
  { key: 'ArrowLeft', ctrlKey: true, description: 'Voltar para lista', category: 'navigation' },
  { key: 'ArrowRight', ctrlKey: true, description: 'Próximo orçamento', category: 'navigation' },
  { key: 'ArrowUp', ctrlKey: true, description: 'Orçamento anterior', category: 'navigation' },
  { key: '/', description: 'Buscar orçamentos', category: 'navigation' },

  // Editing
  { key: 'e', ctrlKey: true, description: 'Editar orçamento', category: 'editing' },
  { key: 's', ctrlKey: true, description: 'Salvar alterações', category: 'editing' },
  { key: 'n', ctrlKey: true, description: 'Novo item', category: 'editing' },
  { key: 'd', ctrlKey: true, description: 'Duplicar orçamento', category: 'editing' },
  { key: 'Enter', description: 'Confirmar edição', category: 'editing' },
  { key: 'Escape', description: 'Cancelar edição', category: 'editing' },

  // Actions
  { key: 'Enter', ctrlKey: true, description: 'Enviar orçamento', category: 'actions' },
  { key: 'p', ctrlKey: true, description: 'Imprimir', category: 'actions' },
  { key: 'd', ctrlKey: true, shiftKey: true, description: 'Download PDF', category: 'actions' },
  { key: 'c', ctrlKey: true, shiftKey: true, description: 'Compartilhar', category: 'actions' },
  { key: 'Delete', ctrlKey: true, description: 'Excluir orçamento', category: 'actions' },

  // View
  { key: 'f11', description: 'Tela cheia', category: 'view' },
  { key: '1', altKey: true, description: 'Visão geral', category: 'view' },
  { key: '2', altKey: true, description: 'Itens', category: 'view' },
  { key: '3', altKey: true, description: 'Cliente', category: 'view' },
  { key: '4', altKey: true, description: 'Comentários', category: 'view' },
  { key: '5', altKey: true, description: 'Histórico', category: 'view' },
  { key: '?', description: 'Ajuda de atalhos', category: 'view' }
];

const SHORTCUT_CATEGORIES = {
  navigation: { label: 'Navegação', icon: ArrowLeft, color: 'text-blue-600' },
  editing: { label: 'Edição', icon: Edit, color: 'text-green-600' },
  actions: { label: 'Ações', icon: Zap, color: 'text-purple-600' },
  view: { label: 'Visualização', icon: Eye, color: 'text-orange-600' }
};

export function BudgetShortcuts({
  budgetId,
  shortcuts = [],
  contextMenuItems = [],
  onCustomShortcut,
  enableKeyboardShortcuts = true,
  enableContextMenu = true,
  showHelpButton = true,
  className
}: BudgetShortcutsProps) {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [customShortcutDialogOpen, setCustomShortcutDialogOpen] = useState(false);
  const [shortcutsEnabled, setShortcutsEnabled] = useState(enableKeyboardShortcuts);
  const [contextMenuEnabled, setContextMenuEnabled] = useState(enableContextMenu);
  const [newShortcut, setNewShortcut] = useState({
    key: '',
    ctrlKey: false,
    shiftKey: false,
    altKey: false,
    description: '',
    category: 'actions' as KeyboardShortcut['category']
  });

  // Combine default shortcuts with custom ones
  const allShortcuts = [
    ...DEFAULT_SHORTCUTS.map(ds => ({
      ...ds,
      action: () => {},
      enabled: true
    })) as KeyboardShortcut[],
    ...shortcuts
  ];

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!shortcutsEnabled) return;

    // Don't trigger shortcuts when typing in input fields
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
      return;
    }

    // Check if any shortcut matches
    const matchingShortcut = allShortcuts.find(shortcut => {
      if (!shortcut.enabled) return false;

      return (
        shortcut.key.toLowerCase() === event.key.toLowerCase() &&
        !!shortcut.ctrlKey === event.ctrlKey &&
        !!shortcut.shiftKey === event.shiftKey &&
        !!shortcut.altKey === event.altKey
      );
    });

    if (matchingShortcut) {
      event.preventDefault();
      matchingShortcut.action();
    }
  }, [shortcutsEnabled, allShortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const formatShortcutKey = (shortcut: KeyboardShortcut) => {
    const parts = [];
    if (shortcut.ctrlKey) parts.push('Ctrl');
    if (shortcut.shiftKey) parts.push('Shift');
    if (shortcut.altKey) parts.push('Alt');

    // Special keys
    const keyMap: Record<string, string> = {
      'ArrowLeft': '←',
      'ArrowRight': '→',
      'ArrowUp': '↑',
      'ArrowDown': '↓',
      'Enter': 'Enter',
      'Escape': 'Esc',
      'Delete': 'Del',
      ' ': 'Space'
    };

    parts.push(keyMap[shortcut.key] || shortcut.key.toUpperCase());
    return parts.join(' + ');
  };

  const handleAddCustomShortcut = () => {
    if (!newShortcut.key || !newShortcut.description) return;

    onCustomShortcut?.(newShortcut);
    setCustomShortcutDialogOpen(false);
    setNewShortcut({
      key: '',
      ctrlKey: false,
      shiftKey: false,
      altKey: false,
      description: '',
      category: 'actions'
    });
  };

  const handleCaptureKey = (event: React.KeyboardEvent) => {
    event.preventDefault();

    const key = event.key;
    if (key.length === 1 || ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Delete', ' '].includes(key)) {
      setNewShortcut({
        ...newShortcut,
        key,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey
      });
    }
  };

  return (
    <div className={className}>
      {/* Help Button */}
      {showHelpButton && (
        <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Keyboard className="h-4 w-4 mr-2" />
              Atalhos
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Keyboard className="h-5 w-5" />
                Atalhos de Teclado
              </DialogTitle>
              <DialogDescription>
                Use estes atalhos para navegar e realizar ações mais rapidamente
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Settings */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Atalhos de Teclado</Label>
                  <p className="text-xs text-muted-foreground">
                    Ativar ou desativar atalhos de teclado globais
                  </p>
                </div>
                <Switch
                  checked={shortcutsEnabled}
                  onCheckedChange={setShortcutsEnabled}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Menu de Contexto</Label>
                  <p className="text-xs text-muted-foreground">
                    Habilitar menu de contexto com clique direito
                  </p>
                </div>
                <Switch
                  checked={contextMenuEnabled}
                  onCheckedChange={setContextMenuEnabled}
                />
              </div>

              {/* Shortcuts by Category */}
              {Object.entries(SHORTCUT_CATEGORIES).map(([category, config]) => {
                const categoryShortcuts = allShortcuts.filter(s => s.category === category && s.enabled);
                if (categoryShortcuts.length === 0) return null;

                const Icon = config.icon;

                return (
                  <div key={category} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${config.color}`} />
                      <h3 className="font-semibold">{config.label}</h3>
                    </div>
                    <div className="grid gap-2">
                      {categoryShortcuts.map((shortcut, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <kbd className="px-2 py-1 text-xs font-mono bg-background border rounded">
                              {formatShortcutKey(shortcut)}
                            </kbd>
                            <span className="text-sm">{shortcut.description}</span>
                          </div>
                          <Switch
                            size="sm"
                            checked={shortcut.enabled}
                            onCheckedChange={(enabled) => {
                              // Toggle shortcut enable/disable
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add Custom Shortcut */}
              <div className="pt-4 border-t">
                <Dialog open={customShortcutDialogOpen} onOpenChange={setCustomShortcutDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Atalho Personalizado
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adicionar Atalho Personalizado</DialogTitle>
                      <DialogDescription>
                        Crie seu próprio atalho de teclado
                      </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                      <div>
                        <Label>Tecla de Atalho</Label>
                        <Input
                          value={formatShortcutKey(newShortcut as any)}
                          onKeyDown={handleCaptureKey}
                          placeholder="Pressione a tecla..."
                          readOnly
                          className="font-mono"
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="ctrl"
                            checked={newShortcut.ctrlKey}
                            onCheckedChange={(checked) => setNewShortcut({ ...newShortcut, ctrlKey: checked })}
                          />
                          <Label htmlFor="ctrl">Ctrl</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="shift"
                            checked={newShortcut.shiftKey}
                            onCheckedChange={(checked) => setNewShortcut({ ...newShortcut, shiftKey: checked })}
                          />
                          <Label htmlFor="shift">Shift</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="alt"
                            checked={newShortcut.altKey}
                            onCheckedChange={(checked) => setNewShortcut({ ...newShortcut, altKey: checked })}
                          />
                          <Label htmlFor="alt">Alt</Label>
                        </div>
                      </div>

                      <div>
                        <Label>Descrição</Label>
                        <Input
                          value={newShortcut.description}
                          onChange={(e) => setNewShortcut({ ...newShortcut, description: e.target.value })}
                          placeholder="O que este atalho faz?"
                        />
                      </div>

                      <div>
                        <Label>Categoria</Label>
                        <select
                          value={newShortcut.category}
                          onChange={(e) => setNewShortcut({ ...newShortcut, category: e.target.value as KeyboardShortcut['category'] })}
                          className="w-full p-2 border rounded-md bg-background text-foreground"
                        >
                          {Object.entries(SHORTCUT_CATEGORIES).map(([key, config]) => (
                            <option key={key} value={key}>
                              {config.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setCustomShortcutDialogOpen(false)}>
                        Cancelar
                      </Button>
                      <Button
                        onClick={handleAddCustomShortcut}
                        disabled={!newShortcut.key || !newShortcut.description}
                      >
                        Adicionar Atalho
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Context Menu Wrapper */}
      {contextMenuEnabled && (
        <ContextMenu>
          <ContextMenuTrigger className="w-full h-full">
            <div className="w-full h-full">
              {/* Your content here */}
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            {contextMenuItems.map((item) => (
              item.separator ? (
                <ContextMenuSeparator key={item.id} />
              ) : (
                <ContextMenuItem
                  key={item.id}
                  onClick={item.action}
                  disabled={item.disabled}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                  {item.badge && (
                    <Badge variant="secondary" className="ml-auto">
                      {item.badge}
                    </Badge>
                  )}
                </ContextMenuItem>
              )
            ))}
          </ContextMenuContent>
        </ContextMenu>
      )}

      {/* Floating Action Button for Quick Access */}
      <div className="fixed bottom-6 right-6 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="lg" className="rounded-full w-14 h-14 shadow-lg">
              <Zap className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => setHelpDialogOpen(true)}>
              <Keyboard className="h-4 w-4 mr-2" />
              Ver Atalhos
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Plus className="h-4 w-4 mr-2" />
              Novo Item
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Copy className="h-4 w-4 mr-2" />
              Duplicar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Send className="h-4 w-4 mr-2" />
              Enviar
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Hook for using shortcuts in components
export function useBudgetShortcuts(shortcuts: KeyboardShortcut[]) {
  const [enabled, setEnabled] = useState(true);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
      return;
    }

    const matchingShortcut = shortcuts.find(shortcut => {
      if (!shortcut.enabled) return false;

      return (
        shortcut.key.toLowerCase() === event.key.toLowerCase() &&
        !!shortcut.ctrlKey === event.ctrlKey &&
        !!shortcut.shiftKey === event.shiftKey &&
        !!shortcut.altKey === event.altKey
      );
    });

    if (matchingShortcut) {
      event.preventDefault();
      matchingShortcut.action();
    }
  }, [enabled, shortcuts]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    enabled,
    setEnabled,
    shortcuts
  };
}